import { useState, useEffect, useCallback, useRef } from 'react';
import type { Inquiry } from '../types';
import { inquiryRepository } from '../data/inquiryRepository';

const PAGE_SIZE = 10;

/**
 * useInquiries フックの戻り値
 */
interface UseInquiriesReturn {
  /** お問い合わせ一覧（全ページ分） */
  inquiries: Inquiry[];
  /** ローディング中（初回） */
  isLoading: boolean;
  /** 追加読み込み中 */
  isLoadingMore: boolean;
  /** エラー */
  error: Error | null;
  /** さらにデータがあるか */
  hasMore: boolean;
  /** 次のページを読み込む */
  loadMore: () => Promise<void>;
  /** 最初から再取得 */
  refetch: () => Promise<void>;
}

/**
 * お問い合わせ一覧を取得するフック（ページネーション対応）
 *
 * 注: homeId は必須。未指定の場合はエラー状態となり API を呼び出さない。
 * 過去バグ: homeId 未指定時にフォールバック `'1'` を渡していたため、本番 API の
 * ULID バリデーションで 400 エラーが発生し、`hasMore=true` 初期値との相互作用で
 * `IntersectionObserver` の無限ループが発生していた。
 */
export const useInquiries = (homeId?: string, token?: string): UseInquiriesReturn => {
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const pageRef = useRef(1);

  const fetchPage = useCallback(async (page: number, isInitial: boolean) => {
    // homeId 必須: 未指定ならエラー状態にして API を呼ばない
    if (!homeId) {
      setError(new Error('homeId が指定されていません'));
      setHasMore(false);
      setIsLoading(false);
      setIsLoadingMore(false);
      return;
    }

    if (isInitial) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    setError(null);

    try {
      const result = await inquiryRepository.getAll(homeId, token, page, PAGE_SIZE);
      if (result.success) {
        const { inquiries: newItems, total } = result.data;
        if (isInitial) {
          setInquiries(newItems);
        } else {
          setInquiries(prev => [...prev, ...newItems]);
        }
        pageRef.current = page;
        // 取得済み件数が合計件数に達したらhasMore=false
        const totalLoaded = isInitial ? newItems.length : (page - 1) * PAGE_SIZE + newItems.length;
        setHasMore(totalLoaded < total);
      } else {
        throw new Error(result.error.message);
      }
    } catch (e) {
      setError(e as Error);
      // エラー時は hasMore=false にしてリトライループを防ぐ
      // （IntersectionObserver による loadMore 連打でエラーが繰り返されるのを防止）
      setHasMore(false);
    } finally {
      if (isInitial) {
        setIsLoading(false);
      } else {
        setIsLoadingMore(false);
      }
    }
  }, [homeId, token]);

  useEffect(() => {
    pageRef.current = 1;
    fetchPage(1, true);
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    await fetchPage(pageRef.current + 1, false);
  }, [fetchPage, isLoadingMore, hasMore]);

  const refetch = useCallback(async () => {
    pageRef.current = 1;
    setHasMore(true);
    setError(null);
    await fetchPage(1, true);
  }, [fetchPage]);

  return {
    inquiries,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
    refetch,
  };
};
